import type { JSX } from 'react';
import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';

type ReturnToState = {
  returnTo?: string;
};

/**
 * A `returnTo` we are willing to send the browser to: a site-relative path only.
 *
 * The value originates in our own `signinRedirect({ state })` call, but it survives a round trip
 * through the IdP, so it is treated as untrusted on the way back. Requiring a single leading `/`
 * rejects both absolute URLs (`https://evil.test/`) and protocol-relative ones (`//evil.test`) —
 * the latter matters specifically because this now drives a document navigation, where the browser
 * would resolve `//evil.test` against the current scheme and leave the origin.
 */
function safeReturnTo(value: unknown): string {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
    ? value
    : '/';
}

/**
 * AuthCallback — handles the OIDC authorization code redirect callback.
 * Mounted at the callback route in the portal router.
 *
 * react-oidc-context automatically processes the callback when this component
 * mounts (it calls UserManager.signinRedirectCallback() internally).
 * On success, we send the browser to state.returnTo or '/'.
 * On error, we render a non-technical error page (no OAuth error details shown).
 *
 * WHY A DOCUMENT NAVIGATION AND NOT `useNavigate`: in the pantry admin suite one callback route at
 * the distribution root is shared by every app, and each app is a SEPARATE SPA bundle served under
 * its own `/<slug>/` prefix (the staff Cognito client registers exactly one callback URL —
 * `https://pantry.<apex>/callback` — so a per-app callback route is not possible). A client-side
 * `navigate('/<slug>/...')` stays inside the ROOT bundle's router, which has no route for another
 * app's path, so a deep link into a suite app used to land on "No route matches URL" with the
 * correct address in the bar. `window.location.replace` re-requests the document, CloudFront serves
 * that app's `index.html`, and its own router takes over. Single-bundle portals are unaffected
 * apart from one extra document load per sign-in, which is once per session.
 *
 * @remarks Styled from `@nrx/auth-ui/auth-ui.css` for the same reason as the guard's two states:
 * Tailwind v4 skips `node_modules` during source detection and workspace packages resolve through
 * it, so the utilities this carried (`text-gray-900`, `text-gray-600`, `text-blue-600`,
 * `text-center text-gray-500`, `min-h-screen`) reached a bundle only where the consuming app's own
 * source used the same class — absent from fx-portal's, present in admin-portal's. Every sign-in
 * passes through here, and the error branch is the sign-in FAILURE path, where a partly unstyled
 * render is hardest to diagnose.
 */
export function AuthCallback(): JSX.Element {
  const auth = useAuth();

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) {
      const state = auth.user?.state as ReturnToState | undefined;
      window.location.replace(safeReturnTo(state?.returnTo));
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.user?.state]);

  if (auth.error) {
    return (
      <div role="main" className="nrx-auth-centered">
        <div className="nrx-auth-callback">
          <h1 className="nrx-auth-callback__title">Sign-in could not be completed</h1>
          <p className="nrx-auth-callback__body">
            There was a problem signing you in. Please try again.
            <br />
            If the problem persists, contact{' '}
            <a href="mailto:support@nourishedrx.com" className="nrx-auth-callback__link">
              NourishedRx support
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Completing sign-in..."
      className="nrx-auth-centered"
    >
      <div className="nrx-auth-callback__body">Completing sign-in…</div>
    </div>
  );
}
